import { useSyncExternalStore } from "react";

/**
 * One shared 1-second tick for every running timer on screen.
 *
 * A per-component interval would mean one timer per entry row, so a task with thirty
 * entries would run thirty of them. The interval here starts on the first subscriber
 * and is cleared when the last one unmounts.
 */
let interval: ReturnType<typeof setInterval> | null = null;
let now = Date.now();
const listeners = new Set<() => void>();

function broadcast() {
    now = Date.now();
    for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    if (interval === null) {
        interval = setInterval(broadcast, 1000);
        // A background tab throttles timers, so the clock can drift badly while hidden.
        // Re-reading the wall clock on the way back snaps it to the truth immediately
        // instead of waiting for the next tick.
        window.addEventListener("visibilitychange", broadcast);
        window.addEventListener("focus", broadcast);
    }
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && interval !== null) {
            clearInterval(interval);
            interval = null;
            window.removeEventListener("visibilitychange", broadcast);
            window.removeEventListener("focus", broadcast);
        }
    };
}

const getSnapshot = () => now;
// On the server there is no ticking; a stable value keeps hydration quiet.
const getServerSnapshot = () => 0;

/** A subscription that never fires, for components with nothing running to show. */
const noopSubscribe = () => () => {};

/**
 * `enabled` matters for the task table: without it every timer cell on screen
 * re-renders once a second even when nothing is running.
 */
export function useTimerTick(enabled = true): number {
    return useSyncExternalStore(enabled ? subscribe : noopSubscribe, getSnapshot, getServerSnapshot);
}
